<template>
  <PageProductDetail
    v-if="product"
    :product="product"
    :configurator-data="configuratorData"
    :menu-item="menuItem"
    :route-configuration="routeConfiguration"
  />
</template>

<script>
import { get } from 'lodash'
import Component from '~/sites/shop/pages/product/_slug'
import gql from 'graphql-tag'

const nuxtI18nPaths = {
  cs: '/produkt/:slug',
  de: '/produkt/:slug',
  en: '/product/:slug',
  sk: '/produkt/:slug',
}

const loadProductConfigurationTypesData = async app => {
  const { data } = await app.apolloProvider.defaultClient.query({
    query: gql`
      query ProductConfigurationType {
        product_configuration_type {
          entities {
            _id
            product_configuration_type_code
            title
            description
            required
            larger_images
          }
        }
      }
    `,
  })
  return data.product_configuration_type.entities
}

const toBaseSlug = s => s?.replace(/-\d+-\d+$/, '')
const getDefaultDimensions = product => {
  const attributes = product?.attributes || {}
  const width = attributes?.limits?.min_dimension?.width ?? attributes?.limits?.min_width ?? attributes?.width
  const height = attributes?.limits?.min_dimension?.height ?? attributes?.limits?.min_height ?? attributes?.height
  if (Number(width) && Number(height)) {
    return { width: Number(width), height: Number(height) }
  }

  return null
}

const PAGE = {
  extends: Component,
  nuxtI18n: {
    paths: nuxtI18nPaths,
  },
  data() {
    return {
      accessoryProducts: [],
      alternativeProducts: [],
      comments: [],
      productReviews: {},
      relatedCategories: [],
      relatedProducts: [],
    }
  },
  head() {
    const parentHeadDef = (Component && (Component.head || (Component.options && Component.options.head))) || null
    const parentHead = typeof parentHeadDef === 'function' ? parentHeadDef.call(this) : parentHeadDef || {}

    const lang = this.$i18n?.locale || 'cs'
    const base = toBaseSlug(this.$route?.params?.slug)
    const dims = getDefaultDimensions(this.product)
    const masterWithDims = `${base}-${dims.width}-${dims.height}`
    const path = (nuxtI18nPaths[lang] || '/produkt/:slug')
      .replace('/:slug', `/${masterWithDims}`)
      .replace(/\/\/+/g, '/')
    const canonicalDomains = this.$store.state.locale?.canonicalDomains || []

    let origin = (canonicalDomains.find(d => d.language === lang) || {}).domain
    if (!origin) {
      if (process.server && this.$ssrContext?.req) {
        const req = this.$ssrContext.req
        const proto = req.headers['x-forwarded-proto'] || (req.connection.encrypted ? 'https' : 'http')
        const host = req.headers['x-forwarded-host'] || req.headers.host
        origin = `${proto}://${host}`
      } else if (process.client) {
        origin = window.location.origin
      }
    }

    const href = `${origin}${path}`.replace(/([^:])[\/]{2,}/g, '$1/')

    return {
      ...parentHead,
      link: [...(parentHead.link || []), { rel: 'canonical', href }],
    }
  },
  async asyncData({ store, params, query, res, app, error }) {
    if (process.server) {
      res.set('X-Inspishop-Cache-Control', `public, max-age=${app.$env.STATELESS_TTL}`)
    }

    const slugParts = params.slug.split('-')
    const baseSlugParts = [...slugParts]
    baseSlugParts.splice(-2, 2)
    const baseSlug = baseSlugParts.join('-')
    const width = Number(slugParts[slugParts.length - 2])
    const height = Number(slugParts[slugParts.length - 1])

    let product = await store.dispatch('product/LOAD_PRODUCT', { slug: params.slug })

    if (!product) {
      let parentProduct = await store.dispatch('product/LOAD_PRODUCT', {
        slug: baseSlug,
        configuration: true,
      })

      if (!parentProduct || parentProduct.length < 1) {
        return app.$pageNotFound({ slug: params.slug, type: 'products' })
      }

      const productConfigurations = get(
        parentProduct.content,
        'product_configurations_priced[0].product_configuration_value_prices',
        [],
      )
      const nearestConfiguration = productConfigurations.find(config => {
        const widthCondition = config.max_width >= width && config.max_width % 5 === 0
        const heightCondition = config.max_height >= height && config.max_width % 5 === 0
        return widthCondition && heightCondition
      })

      if (nearestConfiguration) {
        product = await store.dispatch('product/LOAD_PRODUCT', {
          slug: [baseSlug, nearestConfiguration.max_width, nearestConfiguration.max_height].join('-'),
        })
      }
    }

    if (!product) {
      return app.$pageNotFound({ slug: params.slug, type: 'products' })
    }
    if (product?.error) {
      return error({ statusCode: product.error.statusCode })
    }

    const productConfigurationTypes = await loadProductConfigurationTypesData(app)
    if (productConfigurationTypes.length) {
      product.productConfigurationTypes = productConfigurationTypes
    }

    store.commit('locale/SET_CANONICALS', {
      canonicalSlugs: product.canonicalSlugs,
      nuxtI18nPaths,
    })

    const configuratorData = await store.dispatch('configurator/LOAD_CONFIGURATOR', {
      id: product.content._id,
    })

    const menuItem = product.categoryId
      ? await store
          .dispatch('menu/FIND_MENU_ITEMS', {
            ids: [product.categoryId, ...product.categoryIds],
            menu: 'mainMenu',
          })
          .then(items => items[0])
      : null

    return {
      product,
      configuratorData,
      menuItem,
      routeConfiguration: { width, height },
    }
  },
}

export default PAGE
</script>
